import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from '../modals/DefaultModal';
import useProgramaSituacao from '../hooks/useProgramaSituacao';

const ItemModal = ({ show, onHide, item, onSuccess }) => {
  const [formData, setFormData] = useState({
    cnpjAssociacao: localStorage.getItem('cnpj'),
    idPrograma: '',
    idAcao: '',
    idSubAcao: '',
    idElementoDespesa: '',
    id: '',
    descricao: '',
  });

  const [programas, setProgramas] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [subAcoes, setSubAcoes] = useState([]);
  const [elementosDespesa, setElementosDespesa] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const { isProgramaAtivo } = useProgramaSituacao();
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const programasRes = await axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
          params: { cnpjAssociacao: cnpj },
          headers: { Authorization: `Bearer ${token}` },
        });
        setProgramas(programasRes.data);

        // Carrega elementos de despesa
        const elementosRes = await axios.get(
          `${API_BASE_URL.ELEMENTO_DESPESA}/all`,
          {
            params: { cnpjAssociacao: cnpj },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setElementosDespesa(elementosRes.data);

        if (item) {
          setFormData({
            cnpjAssociacao: item.cnpjAssociacao || cnpj,
            idPrograma: item.idPrograma || '',
            idAcao: item.idAcao || '',
            idSubAcao: item.idSubAcao || '',
            idElementoDespesa: item.idElementoDespesa || '',
            id: item.id || '',
            descricao: (item.descricao || '').toUpperCase(),
          });

          // Carrega ações se já tiver programa selecionado
          if (item.idPrograma) {
            loadAcoes(item.idPrograma);
          }
          // Carrega subações se já tiver ação selecionada
          if (item.idPrograma && item.idAcao) {
            loadSubAcoes(item.idPrograma, item.idAcao);
          }
        } else {
          // Limpa o formulário para novo item
          setFormData({
            cnpjAssociacao: cnpj,
            idPrograma: '',
            idAcao: '',
            idSubAcao: '',
            idElementoDespesa: '',
            id: '',
            descricao: '',
          });
          setAcoes([]);
          setSubAcoes([]);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [item, cnpj]);

  useEffect(() => {
    if (formData.idPrograma) {
      const programaAtivo = isProgramaAtivo(formData.idPrograma);
      setIsProgramaInativo(!programaAtivo);
    } else {
      setIsProgramaInativo(false);
    }
  }, [formData.idPrograma, isProgramaAtivo]);

  // Effect adicional para limpar dados quando o modal é fechado/aberto
  useEffect(() => {
    if (!show) {
      // Reset quando modal é fechado
      setFormData({
        cnpjAssociacao: cnpj,
        idPrograma: '',
        idAcao: '',
        idSubAcao: '',
        idElementoDespesa: '',
        id: '',
        descricao: '',
      });
      setAcoes([]);
      setSubAcoes([]);
    }
  }, [show, cnpj]);

  const loadAcoes = async idPrograma => {
    if (!idPrograma) {
      setAcoes([]);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAcoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar ações:', error);
      setAcoes([]);
    }
  };

  const loadSubAcoes = async (idPrograma, idAcao) => {
    if (!idPrograma || !idAcao) {
      setSubAcoes([]);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL.SUB_ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma, idAcao },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubAcoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar subações:', error);
      setSubAcoes([]);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: name === 'descricao' ? value.toUpperCase() : value,
      };

      // Se mudou o programa, carrega ações e limpa seleção de ação/subação
      if (name === 'idPrograma') {
        setAcoes([]);
        setSubAcoes([]);
        newFormData.idAcao = '';
        newFormData.idSubAcao = '';
        if (value) {
          loadAcoes(value);
        }
      }
      // Se mudou a ação, carrega subações e limpa seleção de subação
      if (name === 'idAcao') {
        setSubAcoes([]);
        newFormData.idSubAcao = '';
        if (newFormData.idPrograma && value) {
          loadSubAcoes(newFormData.idPrograma, value);
        }
      }

      return newFormData;
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.idPrograma && !isProgramaAtivo(formData.idPrograma)) {
      showDefaultModal('error', 'Este programa está ENCERRADO. Não é possível salvar itens para programas encerrados.');
      return;
    }
    const payload = {
      ...formData,
      idSubAcao: formData.idSubAcao ? parseInt(formData.idSubAcao) : null,
      idAcao: formData.idAcao ? parseInt(formData.idAcao) : null,
      idPrograma: formData.idPrograma ? parseInt(formData.idPrograma) : null,
      idElementoDespesa: formData.idElementoDespesa
        ? parseInt(formData.idElementoDespesa)
        : null,
      cnpjAssociacao: cnpj,
    };

    try {
      // const url =
      //   item && item.id
      //     ? `${API_BASE_URL.ITEM}/update`
      //     : `${API_BASE_URL.ITEM}/save`;
      await axios.post(`${API_BASE_URL.ITEM}/save`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showDefaultModal('success', 'Item salvo com sucesso!', () => {
        onSuccess();
        onHide();
      });
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      const errorMessage =
        error.response?.data?.message ||
        'Erro ao salvar o item. Tente novamente.';
      showDefaultModal('error', errorMessage);
    }
  };

  if (!show) return null;
  if (isLoading)
    return <div className="text-center p-3">Carregando dados...</div>;

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div
            className="modal-header bg-primary text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              {item && item.id ? 'Editar Item' : 'Novo Item'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onHide}
            />
          </div>

          {isProgramaInativo && (
            <div className="alert alert-warning mb-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Este programa está <strong>ENCERRADO</strong>. Você pode visualizar os dados, mas não é possível salvar alterações.
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              <div className="row g-3">
                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Programa</label>
                  <select
                    name="idPrograma"
                    className="form-select"
                    value={formData.idPrograma}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {programas.map(programa => (
                      <option key={programa.id} value={programa.id}>
                        {programa.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Ação</label>
                  <select
                    name="idAcao"
                    className="form-select"
                    value={formData.idAcao}
                    onChange={handleChange}
                    disabled={!formData.idPrograma}
                    required
                  >
                    <option value="">Selecione...</option>
                    {acoes.map(acao => (
                      <option key={acao.id} value={acao.id}>
                        {acao.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Subação</label>
                  <select
                    name="idSubAcao"
                    className="form-select"
                    value={formData.idSubAcao}
                    onChange={handleChange}
                    disabled={!formData.idAcao || subAcoes.length === 0}
                  >
                    <option value="">Selecione...</option>
                    {subAcoes.map(subAcao => (
                      <option key={subAcao.id} value={subAcao.id}>
                        {subAcao.descricao}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Caso não haja subação, deixe em branco.
                  </small>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Elemento de Despesa</label>
                  <select
                    name="idElementoDespesa"
                    className="form-select"
                    value={formData.idElementoDespesa}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {elementosDespesa.map(elemento => (
                      <option key={elemento.id} value={elemento.id}>
                        {elemento.id} - {elemento.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Descrição do Item</label>
                  <input
                    type="text"
                    className="form-control"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onHide}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={isProgramaInativo} title={isProgramaInativo ? 'Programa ENCERRADO - não é possível salvar' : undefined}>
                {item && item.id ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </div>
  );
};

export default ItemModal;
